// Deterministic PRNG — xoshiro128** on Uint32 state, seeded via splitmix32.
// Per RFC-0008 §3: integer-only, engine-portable, same seed -> same stream bitwise.
// (Chosen over BigInt splitmix64; Spike 02 Day 2 found BigInt state mixing ~5-10x slower.)

/** Hash an arbitrary seed string to a 32-bit unsigned integer (FNV-1a). */
export function seedFromString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** splitmix32 — used only to expand a single seed word into xoshiro's 4-word state. */
function splitmix32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x9e3779b9) | 0;
    let t = a ^ (a >>> 16);
    t = Math.imul(t, 0x21f0aaad);
    t = t ^ (t >>> 15);
    t = Math.imul(t, 0x735a2d97);
    t = t ^ (t >>> 15);
    return t >>> 0;
  };
}

const rotl = (x: number, k: number): number => ((x << k) | (x >>> (32 - k))) >>> 0;

export interface RNG {
  /** Next 32-bit unsigned integer. */
  u32(): number;
  /** Next float in [0, 1) with 32 bits of entropy. */
  float(): number;
}

/**
 * Build a deterministic RNG from a job seed and a chunk index. The (jobSeed, chunkIndex)
 * pair fully determines the stream, so a chunk produces identical output regardless of
 * which worker or machine runs it — the basis of bitwise reproducibility.
 */
export function makeRNG(jobSeed: number, chunkIndex: number): RNG {
  const expand = splitmix32((jobSeed ^ Math.imul(chunkIndex + 1, 0x9e3779b9)) >>> 0);
  let s0 = expand(), s1 = expand(), s2 = expand(), s3 = expand();

  function u32(): number {
    const result = Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0;
    const t = (s1 << 9) >>> 0;
    s2 = (s2 ^ s0) >>> 0;
    s3 = (s3 ^ s1) >>> 0;
    s1 = (s1 ^ s2) >>> 0;
    s0 = (s0 ^ s3) >>> 0;
    s2 = (s2 ^ t) >>> 0;
    s3 = rotl(s3, 11);
    return result;
  }

  return { u32, float: () => u32() / 4294967296 };
}
